/**
 * Interview Completion — the payoff moment (88-05).
 *
 * When the founder wraps up, we reveal their Inverio Readiness Score RECALCULATED
 * from everything they shared: an animated before → after transition (ring +
 * count-up), the honest composite delta, an evidence-confidence line, and a
 * data-room CTA.
 *
 * HONESTY CONTRACT (design correction 2026-07-08): the completion re-score is a
 * pure recomputation, NOT a guaranteed uplift. The recomputed score may go UP,
 * stay FLAT, or go DOWN. This component renders whichever direction it actually
 * moved with neutral, mentor-warm copy — it never assumes or fakes a positive
 * delta.
 *
 * LOADING-GATE (CLAUDE.md P0-4 spirit): `complete` never returns the after-score
 * (87-06) — the background re-score pins `interview_sessions.score_event_after`
 * asynchronously. While `isAwaitingRescore` is true (completed session, after-event
 * not yet pinned) we show a graceful "recalculating" state. A null after-event
 * during that window is "still computing", NEVER read as "no change" / a zero score.
 */

import React, { useEffect, useRef, useState } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import Animated, {
  FadeIn,
  FadeInDown,
  useSharedValue,
  useAnimatedProps,
  useAnimatedStyle,
  withDelay,
  withTiming,
  withRepeat,
  interpolate,
  interpolateColor,
  Easing,
  useReducedMotion,
} from 'react-native-reanimated';
import { LinearGradient } from 'expo-linear-gradient';
import * as Haptics from 'expo-haptics';
import { useRouter } from 'expo-router';
import { TrendingUp, TrendingDown, Minus, Database, Sparkles, ShieldCheck } from 'lucide-react-native';
import { useQuery } from '@tanstack/react-query';
import { supabase, isSupabaseConfigured } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';
import { isWeb } from '@/lib/platform';
import { useThemeColors } from '@/lib/useThemeColors';
import {
  scoreToLabel,
  labelThemeColor,
  capitalizeLabel,
} from '@/lib/scoring/score-display';
import type { ScoreEventReadModel } from '@/lib/supabase/edge-functions';
import { useInterviewSession } from '@/lib/interview/use-interview';

type Colors = ReturnType<typeof useThemeColors>;
type Composite = ScoreEventReadModel['composite'];

export interface InterviewCompletionProps {
  /** Completed session to poll for the background re-score's after-event. */
  sessionId: string;
  /** Founder-friendly startup name for warm copy. */
  startupName?: string;
  /** Dismiss the reveal + reset the interview container. */
  onDone: () => void;
  /**
   * Resume the interview to cover the remaining areas (re-opens the just-scored
   * session). Shown as the primary CTA only when `stillToCover > 0`.
   */
  onContinue?: () => void;
  /** How many dimensions are still 'not_yet' — drives the "Continue" CTA. */
  stillToCover?: number;
}

// =============================================================================
// Score-event resolution — fetch before/after composites by pinned id
// =============================================================================

interface CompositePair {
  before: Composite | null;
  after: Composite | null;
}

/**
 * Resolve the pinned before/after `score_event` ids into their `composite`
 * blocks in one round-trip. RLS scopes `score_event` to the founder's own
 * startups, so a direct `.in('id', …)` read is safe. Inlined here (not a shared
 * hook) to stay within this plan's `files_modified`.
 */
function useCompositePair(beforeId: string | null, afterId: string | null) {
  return useQuery({
    queryKey: ['interview-completion-scores', beforeId, afterId],
    queryFn: async (): Promise<CompositePair> => {
      const ids = [beforeId, afterId].filter((v): v is string => !!v);
      if (mode.isMock || !isSupabaseConfigured() || ids.length === 0) {
        return { before: null, after: null };
      }
      const { data, error } = await supabase
        .from('score_event' as never)
        .select('id, composite')
        .in('id', ids);
      if (error) throw error;
      const rows = (data ?? []) as unknown as { id: string; composite: Composite }[];
      const map = new Map(rows.map((r) => [r.id, r.composite]));
      return {
        before: beforeId ? map.get(beforeId) ?? null : null,
        after: afterId ? map.get(afterId) ?? null : null,
      };
    },
    enabled: !!beforeId || !!afterId,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// InterviewCompletion
// =============================================================================

export function InterviewCompletion({
  sessionId,
  startupName,
  onDone,
  onContinue,
  stillToCover = 0,
}: InterviewCompletionProps) {
  const colors = useThemeColors();
  const router = useRouter();

  const session = useInterviewSession(sessionId);
  const beforeId = session.data?.scoreEventBefore ?? null;
  const afterId = session.data?.scoreEventAfter ?? null;

  // Loading-gate: default to "awaiting" until the poll resolves so a not-yet-loaded
  // session never renders as a settled "no change". Stays awaiting until the
  // after-event is pinned (isAwaitingRescore flips false with score_event_after set).
  const isAwaiting = session.data ? session.data.isAwaitingRescore : true;

  const pair = useCompositePair(beforeId, afterId);
  const after = pair.data?.after ?? null;
  const before = pair.data?.before ?? null;

  // Ready only once the after-event is pinned AND its composite has loaded.
  const ready = !isAwaiting && !!after && !pair.isLoading;

  if (!ready) {
    return <Recalculating colors={colors} startupName={startupName} onDone={onDone} />;
  }

  return (
    <Reveal
      colors={colors}
      startupName={startupName}
      before={before}
      after={after!}
      onConnectDataRoom={() => {
        Haptics.selectionAsync().catch(() => {});
        router.push('/(startup)/dataroom');
      }}
      onDone={onDone}
      onContinue={onContinue}
      stillToCover={stillToCover}
    />
  );
}

// =============================================================================
// Recalculating — graceful loading-gate state (NEVER a zero/empty score)
// =============================================================================

function Recalculating({
  colors,
  startupName,
  onDone,
}: {
  colors: Colors;
  startupName?: string;
  onDone: () => void;
}) {
  const reduced = useReducedMotion();
  const pulse = useSharedValue(0);
  useEffect(() => {
    if (reduced) return;
    pulse.value = withRepeat(withTiming(1, { duration: 1100, easing: Easing.inOut(Easing.ease) }), -1, true);
  }, [reduced, pulse]);

  const ringStyle = useAnimatedStyle(() => ({
    opacity: 0.55 + pulse.value * 0.45,
    transform: [{ scale: 0.96 + pulse.value * 0.08 }],
  }));

  return (
    <View className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.surface.primary }}>
      <Animated.View
        style={[
          {
            width: 96,
            height: 96,
            borderRadius: 48,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 28,
            backgroundColor: colors.primary[50],
            borderWidth: 2,
            borderColor: colors.primary[500],
          },
          ringStyle,
        ]}
      >
        <Sparkles size={38} color={colors.primary[600]} />
      </Animated.View>
      <Text className="text-xl font-bold text-center mb-2" style={{ color: colors.text.primary }}>
        Recalculating your score
      </Text>
      <Text className="text-sm text-center leading-relaxed" style={{ color: colors.text.tertiary, maxWidth: 320 }}>
        We're weaving everything you shared about {startupName ?? 'your startup'} into your Inverio
        Readiness Score. This takes a moment — hang tight.
      </Text>
      <Pressable onPress={onDone} hitSlop={10} className="mt-9 px-6 py-2.5">
        <Text className="text-sm font-semibold" style={{ color: colors.text.tertiary }}>
          I'll check back later
        </Text>
      </Pressable>
    </View>
  );
}

// =============================================================================
// Reveal — the animated before → after payoff (honest, direction-aware)
// =============================================================================

type Direction = 'up' | 'flat' | 'down';

function Reveal({
  colors,
  startupName,
  before,
  after,
  onConnectDataRoom,
  onDone,
  onContinue,
  stillToCover = 0,
}: {
  colors: Colors;
  startupName?: string;
  before: Composite | null;
  after: Composite;
  onConnectDataRoom: () => void;
  onDone: () => void;
  onContinue?: () => void;
  stillToCover?: number;
}) {
  const reduced = useReducedMotion();

  const afterScore = Math.round(after.finalScore);
  const afterBand = after.ventureLabel ?? scoreToLabel(afterScore);
  const afterColor = labelThemeColor(afterBand, colors);

  const hasBefore = before !== null;
  const beforeScore = hasBefore ? Math.round(before!.finalScore) : afterScore;
  const beforeBand = hasBefore ? (before!.ventureLabel ?? scoreToLabel(beforeScore)) : afterBand;
  const beforeColor = labelThemeColor(beforeBand, colors);

  const delta = afterScore - beforeScore;
  const direction: Direction = !hasBefore || delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down';

  // Evidence-confidence movement (fourAxis.evidenceQuality), honest both ways.
  const afterConf = Math.round(after.fourAxis?.evidenceQuality ?? 0);
  const beforeConf = hasBefore ? Math.round(before!.fourAxis?.evidenceQuality ?? 0) : afterConf;
  const confDelta = afterConf - beforeConf;

  // Neutral haptic on reveal — a settled result, not a "you won" celebration.
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch(() => {});
  }, []);

  const dirCopy = directionCopy(direction, Math.abs(delta), hasBefore);

  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.primary }}>
      {/* Soft band-tinted glow behind the score — atmosphere, not flat fill */}
      <LinearGradient
        colors={[`${afterColor}22`, `${afterColor}00`]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 340 }}
        pointerEvents="none"
      />
      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          flexGrow: 1,
          alignItems: 'center',
          paddingHorizontal: 24,
          paddingTop: isWeb ? 48 : 40,
          paddingBottom: 32,
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Eyebrow */}
        <Animated.View entering={reduced ? undefined : FadeIn.duration(400)} className="items-center mb-5">
          <View
            className="flex-row items-center px-3 py-1.5 rounded-full"
            style={{ backgroundColor: colors.primary[50] }}
          >
            <Sparkles size={13} color={colors.primary[600]} />
            <Text className="text-[11px] font-bold ml-1.5" style={{ color: colors.primary[600], letterSpacing: 0.4 }}>
              SCORE RECALCULATED
            </Text>
          </View>
        </Animated.View>

        {/* Animated ring — counts before → after and shifts band color */}
        <AnimatedScoreRing
          fromScore={beforeScore}
          toScore={afterScore}
          fromColor={beforeColor}
          toColor={afterColor}
          reduced={reduced}
          colors={colors}
        />

        {/* Band label */}
        <Animated.View
          entering={reduced ? undefined : FadeInDown.delay(700).duration(500)}
          className="mt-4 mb-1"
        >
          <Text className="text-lg font-bold" style={{ color: afterColor }}>
            {capitalizeLabel(afterBand)}
          </Text>
        </Animated.View>

        {/* Before → after transition line (only when we have a real anchor) */}
        {hasBefore && (
          <Animated.View
            entering={reduced ? undefined : FadeInDown.delay(820).duration(500)}
            className="flex-row items-center mt-1"
          >
            <Text className="text-sm font-semibold" style={{ color: colors.text.tertiary }}>
              {beforeScore}
            </Text>
            <Text className="text-sm mx-2" style={{ color: colors.text.tertiary }}>→</Text>
            <Text className="text-sm font-semibold" style={{ color: afterColor }}>
              {afterScore}
            </Text>
            <DeltaChip direction={direction} delta={delta} colors={colors} />
          </Animated.View>
        )}

        {/* Honest, direction-aware headline + mentor thank-you */}
        <Animated.View
          entering={reduced ? undefined : FadeInDown.delay(940).duration(500)}
          className="items-center mt-7"
          style={{ maxWidth: 360 }}
        >
          <Text className="text-2xl font-bold text-center mb-2.5" style={{ color: colors.text.primary }}>
            {dirCopy.headline}
          </Text>
          <Text className="text-[15px] text-center leading-relaxed" style={{ color: colors.text.secondary }}>
            Thank you for walking us through {startupName ?? 'your startup'}. {dirCopy.body}
          </Text>
        </Animated.View>

        {/* Evidence-confidence movement — honest both ways */}
        <Animated.View
          entering={reduced ? undefined : FadeInDown.delay(1060).duration(500)}
          className="flex-row items-center mt-5 px-3.5 py-2 rounded-xl"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <ShieldCheck size={15} color={colors.text.tertiary} />
          <Text className="text-[13px] ml-2" style={{ color: colors.text.secondary }}>
            {confidenceCopy(confDelta, afterConf, hasBefore)}
          </Text>
        </Animated.View>

        {/* CTA */}
        <Animated.View
          entering={reduced ? undefined : FadeInDown.delay(1200).duration(500)}
          className="w-full items-center mt-9"
          style={{ maxWidth: 400 }}
        >
          {onContinue && stillToCover > 0 ? (
            <>
              {/* Partial wrap-up — this score is on partial evidence; coming back
                  to cover the rest is the strongest, most direct lever. */}
              <View className="w-full rounded-2xl overflow-hidden" style={{ backgroundColor: colors.primary[600] }}>
                <Pressable
                  onPress={onContinue}
                  className="flex-row items-center justify-center px-5"
                  style={{ height: 54 }}
                >
                  <Sparkles size={18} color={colors.text.inverse} />
                  <Text className="text-[15px] font-bold ml-2.5" style={{ color: colors.text.inverse }}>
                    Continue interview
                  </Text>
                </Pressable>
              </View>
              <Text className="text-[12px] text-center mt-2.5" style={{ color: colors.text.tertiary, maxWidth: 320 }}>
                {stillToCover} {stillToCover === 1 ? 'area is' : 'areas are'} still to cover — a few more
                answers will strengthen this score.
              </Text>

              <Pressable onPress={onConnectDataRoom} hitSlop={10} className="mt-4 flex-row items-center px-6 py-2.5">
                <Database size={15} color={colors.text.tertiary} />
                <Text className="text-sm font-semibold ml-2" style={{ color: colors.text.tertiary }}>
                  Connect a data room
                </Text>
              </Pressable>
              <Pressable onPress={onDone} hitSlop={10} className="mt-1 px-6 py-2">
                <Text className="text-sm font-semibold" style={{ color: colors.text.tertiary }}>
                  Done for now
                </Text>
              </Pressable>
            </>
          ) : (
            <>
              {/* Fully covered — data room is the next lever. */}
              <View className="w-full rounded-2xl overflow-hidden" style={{ backgroundColor: colors.primary[600] }}>
                <Pressable
                  onPress={onConnectDataRoom}
                  className="flex-row items-center justify-center px-5"
                  style={{ height: 54 }}
                >
                  <Database size={18} color={colors.text.inverse} />
                  <Text className="text-[15px] font-bold ml-2.5" style={{ color: colors.text.inverse }}>
                    Connect a data room
                  </Text>
                </Pressable>
              </View>
              <Text className="text-[12px] text-center mt-2.5" style={{ color: colors.text.tertiary, maxWidth: 320 }}>
                Documents let us verify what you shared — the strongest way to move your score.
              </Text>

              <Pressable onPress={onDone} hitSlop={10} className="mt-4 px-6 py-2.5">
                <Text className="text-sm font-semibold" style={{ color: colors.text.tertiary }}>
                  Done for now
                </Text>
              </Pressable>
            </>
          )}
        </Animated.View>
      </ScrollView>
    </View>
  );
}

// =============================================================================
// Copy helpers — honest across up / flat / down
// =============================================================================

function directionCopy(
  direction: Direction,
  absDelta: number,
  hasBefore: boolean,
): { headline: string; body: string } {
  if (!hasBefore) {
    return {
      headline: 'Here’s your readiness score',
      body: 'It’s now grounded in the story you just told us — a real starting point you can build on.',
    };
  }
  switch (direction) {
    case 'up':
      return {
        headline: 'Your score moved up',
        body: `What you shared added ${absDelta} point${absDelta === 1 ? '' : 's'} of readiness. The clearer your story, the more it shows.`,
      };
    case 'down':
      return {
        headline: 'Your score is now sharper',
        body: `With the fuller picture it settled ${absDelta} point${absDelta === 1 ? '' : 's'} lower — a more precise read, and a clear place to focus next.`,
      };
    case 'flat':
    default:
      return {
        headline: 'Your score held steady',
        body: 'Everything you shared is now on record and reinforcing your readiness — a solid, evidence-backed footing.',
      };
  }
}

function confidenceCopy(confDelta: number, afterConf: number, hasBefore: boolean): string {
  if (!hasBefore || confDelta === 0) {
    return `Evidence confidence at ${afterConf}%.`;
  }
  if (confDelta > 0) {
    return `Evidence confidence rose ${confDelta} points, to ${afterConf}%.`;
  }
  return `Evidence confidence eased ${Math.abs(confDelta)} points, to ${afterConf}%.`;
}

// =============================================================================
// DeltaChip — honest up / flat / down pill
// =============================================================================

function DeltaChip({ direction, delta, colors }: { direction: Direction; delta: number; colors: Colors }) {
  const tint =
    direction === 'up' ? colors.success : direction === 'down' ? colors.error : colors.text.tertiary;
  const Icon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus;
  const label = direction === 'flat' ? 'No change' : `${delta > 0 ? '+' : ''}${delta}`;

  return (
    <View
      className="flex-row items-center ml-2.5 px-2 py-0.5 rounded-md"
      style={{ backgroundColor: `${tint}18` }}
    >
      <Icon size={12} color={tint} />
      <Text className="text-[12px] font-bold ml-1" style={{ color: tint }}>
        {label}
      </Text>
    </View>
  );
}

// =============================================================================
// AnimatedScoreRing — SVG ring + JS count-up (cross-platform: web + native)
// =============================================================================

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

function AnimatedScoreRing({
  fromScore,
  toScore,
  fromColor,
  toColor,
  reduced,
  colors,
}: {
  fromScore: number;
  toScore: number;
  fromColor: string;
  toColor: string;
  reduced: boolean;
  colors: Colors;
}) {
  const SIZE = isWeb ? 180 : 168;
  const STROKE = 14;
  const radius = (SIZE - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;

  const progress = useSharedValue(reduced ? 1 : 0);
  useEffect(() => {
    if (reduced) {
      progress.value = 1;
      return;
    }
    progress.value = withDelay(300, withTiming(1, { duration: 1300, easing: Easing.out(Easing.cubic) }));
  }, [reduced, progress, toScore]);

  const arcProps = useAnimatedProps(() => {
    const shown = fromScore + (toScore - fromScore) * progress.value;
    return {
      strokeDashoffset: circumference * (1 - shown / 100),
      stroke: interpolateColor(progress.value, [0, 1], [fromColor, toColor]),
    };
  });

  const scaleStyle = useAnimatedStyle(() => ({
    transform: [{ scale: reduced ? 1 : interpolate(progress.value, [0, 0.3, 1], [0.9, 1.02, 1]) }],
  }));

  return (
    <Animated.View style={[{ width: SIZE, height: SIZE, alignItems: 'center', justifyContent: 'center' }, scaleStyle]}>
      <Svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ position: 'absolute' }}>
        {/* Track */}
        <Circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={radius}
          stroke={colors.border.light}
          strokeWidth={STROKE}
          fill="none"
        />
        {/* Animated progress arc — rotated so it starts at 12 o'clock */}
        <AnimatedCircle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={radius}
          strokeWidth={STROKE}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circumference}
          animatedProps={arcProps}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
        />
      </Svg>
      <CountUp from={fromScore} to={toScore} reduced={reduced} color={colors.text.primary} />
      <Text
        className="text-[11px] font-semibold"
        style={{ color: colors.text.tertiary, marginTop: 2, letterSpacing: 1 }}
      >
        / 100
      </Text>
    </Animated.View>
  );
}

/**
 * Cross-platform count-up. Drives its OWN state via requestAnimationFrame so only
 * this small node re-renders (reanimated's animated-text trick is native-only and
 * silently no-ops on RN-Web — this works everywhere). Honors reduced motion.
 */
function CountUp({
  from,
  to,
  reduced,
  color,
}: {
  from: number;
  to: number;
  reduced: boolean;
  color: string;
}) {
  const [val, setVal] = useState(reduced ? to : from);

  useEffect(() => {
    if (reduced) {
      setVal(to);
      return;
    }
    const duration = 1300;
    const startDelay = 300;
    let raf = 0;
    let start = 0;
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
    const tick = (now: number) => {
      if (!start) start = now;
      const elapsed = now - start;
      const t = Math.min(1, Math.max(0, (elapsed - startDelay) / duration));
      setVal(Math.round(from + (to - from) * easeOutCubic(t)));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [from, to, reduced]);

  return (
    <Text style={{ color, fontSize: isWeb ? 56 : 52, fontWeight: '800', letterSpacing: -1 }}>
      {val}
    </Text>
  );
}
